import React from 'react';
import {
  Users,
  Building2,
  GitBranch,
  Link as LinkIcon,
  RefreshCw,
  Activity,
  BarChart2,
  FileText,
  Zap,
  Mail,
  Database,
  Bell,
  Flag,
  PanelLeft,
  UserCheck,
  Ticket,
  Video,
  Tag,
  CreditCard,
  LineChart,
} from 'lucide-react';

export type AdminMenuActionId = 'generate_free_trial' | 'resync_billing' | 'assign_subscription';

export type AdminMenuAccess = 'admin' | 'employee' | 'admin_employee';

export interface AdminMenuCatalogItem {
  key: string;
  label: string;
  description: string;
  href?: string;
  actionId?: AdminMenuActionId;
  icon: React.ReactNode;
  group: string;
  groupAccentBorder: string;
  accent: string;
  defaultStarred: boolean;
  access?: AdminMenuAccess;
}

export const canAccessAdminMenuItem = (
  item: Pick<AdminMenuCatalogItem, 'access'>,
  viewer: { isAdmin: boolean; isEmployee: boolean },
): boolean => {
  const access = item.access ?? 'admin';
  if (access === 'employee') return viewer.isEmployee;
  if (access === 'admin_employee') return viewer.isAdmin && viewer.isEmployee;
  return viewer.isAdmin;
};

export const ADMIN_MENU_CATALOG: AdminMenuCatalogItem[] = [
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Active companies, feature usage, trial conversion, and marketing-site traffic',
    href: '/system/analytics',
    icon: <LineChart size={32} />,
    group: 'Insights',
    groupAccentBorder: 'border-rs-green',
    accent: 'text-rs-green',
    defaultStarred: true,
    access: 'employee',
  },
  {
    key: 'user_management',
    label: 'User Management',
    description: 'Manage users, memberships, and access levels',
    href: '/user-management',
    icon: <Users size={32} />,
    group: 'User & Access Management',
    groupAccentBorder: 'border-rs-green',
    accent: 'text-rs-green',
    defaultStarred: true,
  },
  {
    key: 'companies',
    label: 'Companies',
    description: 'Manage client companies and their members',
    href: '/system/companies',
    icon: <Building2 size={32} />,
    group: 'User & Access Management',
    groupAccentBorder: 'border-rs-green',
    accent: 'text-rs-green',
    defaultStarred: true,
  },
  {
    key: 'parent_groups',
    label: 'Parent Groups',
    description: 'Organize companies into parent group hierarchies',
    href: '/system/parent-groups',
    icon: <GitBranch size={32} />,
    group: 'User & Access Management',
    groupAccentBorder: 'border-rs-green',
    accent: 'text-rs-green',
    defaultStarred: false,
  },
  {
    key: 'generate_free_trial',
    label: 'Generate Free Trial',
    description: 'Create time-limited trial access links',
    actionId: 'generate_free_trial',
    icon: <LinkIcon size={32} />,
    group: 'User & Access Management',
    groupAccentBorder: 'border-rs-green',
    accent: 'text-rs-green',
    defaultStarred: true,
  },
  {
    key: 'refresh_data',
    label: 'Refresh Data',
    description: 'Manually trigger data refresh pipelines',
    href: '/refresh-data',
    icon: <RefreshCw size={32} />,
    group: 'Data Operations',
    groupAccentBorder: 'border-rs-blue',
    accent: 'text-rs-blue',
    defaultStarred: true,
  },
  {
    key: 'data_refresh_status',
    label: 'Data Refresh Status',
    description: 'Monitor the status of data refresh jobs',
    href: '/refresh-data',
    icon: <Activity size={32} />,
    group: 'Data Operations',
    groupAccentBorder: 'border-rs-blue',
    accent: 'text-rs-blue',
    defaultStarred: false,
  },
  {
    key: 'demo_capture',
    label: 'Capture Demo Snapshot',
    description: 'Record API responses for the public interactive demo',
    href: '/system/demo-capture',
    icon: <Video size={32} />,
    group: 'Data Operations',
    groupAccentBorder: 'border-rs-blue',
    accent: 'text-rs-blue',
    defaultStarred: false,
  },
  {
    key: 'demo_analytics',
    label: 'Demo Analytics',
    description: 'See how many visitors go through the public demo, and how far they get',
    href: '/system/demo-analytics',
    icon: <BarChart2 size={32} />,
    group: 'Data Operations',
    groupAccentBorder: 'border-rs-blue',
    accent: 'text-rs-blue',
    defaultStarred: false,
  },
  {
    key: 'commentary_data',
    label: 'Commentary Data',
    description: 'Edit driver reports, ACC commentary, and forecast text',
    href: '/commentary-data',
    icon: <FileText size={32} />,
    group: 'Content Management',
    groupAccentBorder: 'border-rs-salmon',
    accent: 'text-rs-salmon',
    defaultStarred: true,
  },
  {
    key: 'reports',
    label: 'Reports',
    description: 'Manage report abbreviations, sort order, and access controls',
    href: '/system/reports',
    icon: <BarChart2 size={32} />,
    group: 'Platform',
    groupAccentBorder: 'border-rs-green',
    accent: 'text-rs-green',
    defaultStarred: false,
  },
  {
    key: 'feature_flags',
    label: 'Feature Flags',
    description: 'Control feature visibility by role and membership tier',
    href: '/system/feature-flags',
    icon: <Flag size={32} />,
    group: 'Platform',
    groupAccentBorder: 'border-rs-green',
    accent: 'text-rs-green',
    defaultStarred: false,
  },
  {
    key: 'navigation',
    label: 'Navigation',
    description: 'Control which pages appear in the left nav and their membership requirements',
    href: '/system/nav-items',
    icon: <PanelLeft size={32} />,
    group: 'Platform',
    groupAccentBorder: 'border-rs-green',
    accent: 'text-rs-green',
    defaultStarred: false,
  },
  {
    key: 'system_tickets',
    label: 'System Tickets',
    description: 'File app and engineering requests onto the Azure DevOps board',
    href: '/system/tickets',
    icon: <Ticket size={32} />,
    group: 'Integrations',
    groupAccentBorder: 'border-purple-400',
    accent: 'text-purple-400',
    defaultStarred: false,
    access: 'employee',
  },
  {
    key: 'email_manager',
    label: 'Email Manager',
    description: 'Manage email templates and notification settings',
    href: '/system/email-manager',
    icon: <Mail size={32} />,
    group: 'Integrations',
    groupAccentBorder: 'border-purple-400',
    accent: 'text-purple-400',
    defaultStarred: false,
  },
  {
    key: 'hubspot_sync',
    label: 'HubSpot Sync',
    description: 'Sync user subscription data with HubSpot CRM',
    href: '/system/hubspot-sync',
    icon: <Database size={32} />,
    group: 'Integrations',
    groupAccentBorder: 'border-purple-400',
    accent: 'text-purple-400',
    defaultStarred: false,
  },
  {
    key: 'available_subscriptions',
    label: 'Available Subscriptions',
    description: 'Manage email subscriptions shown to users in Account Settings',
    href: '/system/available-subscriptions',
    icon: <Bell size={32} />,
    group: 'Integrations',
    groupAccentBorder: 'border-purple-400',
    accent: 'text-purple-400',
    defaultStarred: false,
  },
  {
    key: 'notification_test',
    label: 'Notification Test',
    description: 'Send test in-app and email notifications to yourself to verify appearance',
    href: '/system/notification-test',
    icon: <Zap size={32} />,
    group: 'Integrations',
    groupAccentBorder: 'border-purple-400',
    accent: 'text-purple-400',
    defaultStarred: false,
  },
  {
    key: 'resync_billing',
    label: 'Resync Billing',
    description: 'Pull latest subscription state from HubSpot for all companies.',
    icon: <RefreshCw size={24} />,
    actionId: 'resync_billing',
    group: 'Billing & Subscriptions',
    groupAccentBorder: 'border-rs-salmon',
    accent: 'text-rs-salmon',
    defaultStarred: false,
  },
  {
    key: 'assign_subscription',
    label: 'Assign Subscription',
    description: 'Manually link a HubSpot subscription to a user when auto-sync fails.',
    icon: <UserCheck size={24} />,
    actionId: 'assign_subscription',
    group: 'Billing & Subscriptions',
    groupAccentBorder: 'border-rs-salmon',
    accent: 'text-rs-salmon',
    defaultStarred: false,
  },
  {
    key: 'promo_codes',
    label: 'Promo Codes',
    description: 'Manage promo codes and the HS Payment Links they unlock',
    href: '/system/promo-codes',
    icon: <Tag size={32} />,
    group: 'Billing & Subscriptions',
    groupAccentBorder: 'border-rs-salmon',
    accent: 'text-rs-salmon',
    defaultStarred: false,
  },
  {
    key: 'admin_subscriptions',
    label: 'Subscriptions',
    description: 'View all active self-serve subscriptions, plan, status, and billing dates (Employee + Admin only)',
    href: '/system/subscriptions',
    icon: <CreditCard size={32} />,
    group: 'Billing & Subscriptions',
    groupAccentBorder: 'border-rs-salmon',
    accent: 'text-rs-salmon',
    defaultStarred: false,
    access: 'admin_employee',
  },
];

export const ADMIN_MENU_CATALOG_BY_KEY: Record<string, AdminMenuCatalogItem> =
  ADMIN_MENU_CATALOG.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {} as Record<string, AdminMenuCatalogItem>);

export const ADMIN_MENU_GROUP_ORDER: { name: string; accentBorder: string }[] = [
  { name: 'Insights', accentBorder: 'border-rs-green' },
  { name: 'User & Access Management', accentBorder: 'border-rs-green' },
  { name: 'Billing & Subscriptions', accentBorder: 'border-rs-salmon' },
  { name: 'Data Operations', accentBorder: 'border-rs-blue' },
  { name: 'Content Management', accentBorder: 'border-rs-salmon' },
  { name: 'Platform', accentBorder: 'border-rs-green' },
  { name: 'Integrations', accentBorder: 'border-purple-400' },
];

export const ADMIN_MENU_DEFAULT_STARRED_ORDER: string[] = [
  'analytics',
  'companies',
  'user_management',
  'generate_free_trial',
  'refresh_data',
  'commentary_data',
];
